import { useRouter } from 'next/router';
import React from 'react';

import { ROUTES } from '@common/constant/routeConstant';
import { ACADEMIC_TYPE } from '@common/type/CourseTypes';
import { GtmAcademicType } from '@common/type/GoogleTagType';
import { Box, BoxProps, Button, Chip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import config from 'src/config';

import EnrolledAuditBox from './enrolledAuditBox';
import PriceBox from './priceBox';

import ConfirmEmailModal from '@components/atoms/dialog/confirmEmail';
import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import AuthorizeModal from '@components/share/modal/authorizeModal/AuthorizeModal';
import BaseModalWrapper from '@components/share/modal/baseModalWrapper';
import { setAcademicEnrollCookie } from '@helpers/auth';
import { createGoogleAnalyticItem, sendAddToCardCourseDetail } from '@helpers/googleTagManager';
import { useBreakpointV2 } from '@helpers/use';
import useDialogWithoutUI from '@hooks/useDialog';
import { useIdsProfile } from '@hooks/useIdsProfile';
import { useProfile } from '@hooks/useProfile';
import { AcademicCourseEnrollmentStatus, CourseDetailEntity } from '@redux/course/type/courseDetailType';

type CourseAcademicBannerPurchaseButtonProps = {
  name?: string;
  idName: string;
  sx?: BoxProps['sx'];
  price: CourseDetailEntity['price'];
  gtmContentId?: string;
  purchaseType?: ACADEMIC_TYPE.AUDIT | ACADEMIC_TYPE.CREDIT;
  enrollmentStatus: AcademicCourseEnrollmentStatus;
  disabled?: boolean;
  courseDetailData?: CourseDetailEntity;
};
const PurchaseButton = ({
  name,
  idName,
  enrollmentStatus,
  price,
  gtmContentId,
  disabled,
  sx = {},
  purchaseType = ACADEMIC_TYPE.AUDIT,
  courseDetailData,
}: CourseAcademicBannerPurchaseButtonProps) => {
  const theme = useTheme();
  const { t } = useTranslation('store_course');
  const { profile } = useProfile();
  const { idsProfile } = useIdsProfile();
  const router = useRouter();
  const { isMobile } = useBreakpointV2();
  const authorizeDialog = useDialogWithoutUI({});
  const confirmEmailDialog = useDialogWithoutUI({});

  const isAudit = purchaseType === 'audit';

  const icon = isAudit ? 'fa-light fa-book-open-cover' : 'fa-light fa-graduation-cap';
  const title = isAudit
    ? t('course_detail.header_banner.purchase.audit.title')
    : t('course_detail.header_banner.purchase.credit.title');
  const content = isAudit
    ? t('course_detail.header_banner.purchase.audit.content')
    : t('course_detail.header_banner.purchase.credit.content');

  const isEnrolledAudit =
    [
      AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLED,
      AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLMENT_EXPIRED,
      AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_ENROLLMENT_EXPIRED,
      AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PASSED_REGRADE,
      AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_REGRADE,
    ].includes(enrollmentStatus) && isAudit;

  const textByEnrollmentStatus = {
    [AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLED]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.enrolled.button.text'),
        credit: t('course_detail.header_banner.purchase.credit.button.text'),
      },
      price: {
        audit: null,
        credit: price.upgrade,
      },
      academicType: isAudit ? ACADEMIC_TYPE.AUDIT : ACADEMIC_TYPE.UPGRADE,
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLMENT_EXPIRED]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.enrolled.button.text'),
        credit: t('course_detail.header_banner.purchase.credit.button.text'),
      },
      price: {
        audit: null,
        credit: price.credit,
      },
      academicType: isAudit ? ACADEMIC_TYPE.AUDIT : ACADEMIC_TYPE.CREDIT_PLUS,
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_ENROLLED]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.enrolled.button.text'),
        credit: t('course_detail.header_banner.purchase.enrolled.button.text'),
      },
      price: {
        audit: null,
        credit: null,
      },
      academicType: null,
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PENDING]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.enrolled.button.text'),
        credit: t('course_detail.header_banner.purchase.enrolled.button.text'),
      },
      price: {
        audit: null,
        credit: null,
      },
      academicType: null,
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_ENROLLMENT_EXPIRED]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.enrolled.button.text'),
        credit: t('course_detail.header_banner.purchase.regrade.button.text'),
      },
      price: {
        audit: null,
        credit: price.credit,
      },
      academicType: isAudit ? ACADEMIC_TYPE.AUDIT : ACADEMIC_TYPE.CREDIT_PLUS,
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PASSED]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.enrolled.button.text'),
        credit: t('course_detail.header_banner.purchase.regrade.button.text'),
      },
      price: {
        audit: null,
        credit: price.credit,
      },
      academicType: null,
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PASSED_REGRADE]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.enrolled.button.text'),
        credit: t('course_detail.header_banner.purchase.regrade.button.text'),
      },
      price: {
        audit: null,
        credit: price.credit,
      },
      academicType: isAudit ? ACADEMIC_TYPE.AUDIT : ACADEMIC_TYPE.REGRADE,
    },
    [AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_REGRADE]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.enrolled.button.text'),
        credit: t('course_detail.header_banner.purchase.regrade.button.text'),
      },
      price: {
        audit: null,
        credit: price.credit,
      },
      academicType: isAudit ? ACADEMIC_TYPE.AUDIT : ACADEMIC_TYPE.REGRADE,
    },
    [AcademicCourseEnrollmentStatus.AVAILABLE_REGISTER]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.audit.button.text'),
        credit: t('course_detail.header_banner.purchase.credit.button.text'),
      },
      price: {
        audit: price.audit,
        credit: price.credit,
      },
      academicType: isAudit ? ACADEMIC_TYPE.AUDIT : ACADEMIC_TYPE.CREDIT,
    },
    [AcademicCourseEnrollmentStatus.PUBLISHED]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.unavailable.published.button.text'),
        credit: t('course_detail.header_banner.purchase.unavailable.published.button.text'),
      },
      price: {
        audit: price.audit,
        credit: price.credit,
      },
      academicType: null,
    },
    [AcademicCourseEnrollmentStatus.UNPUBLISHED]: {
      buttonText: {
        audit: t('course_detail.header_banner.purchase.unavailable.unpublished.button.text'),
        credit: t('course_detail.header_banner.purchase.unavailable.unpublished.button.text'),
      },
      price: {
        audit: price.audit,
        credit: price.credit,
      },
      academicType: null,
    },
  };

  const buttonText = textByEnrollmentStatus[enrollmentStatus].buttonText[purchaseType];
  const priceByEnrollmentStatus = textByEnrollmentStatus[enrollmentStatus].price[purchaseType];

  const fullPrice = priceByEnrollmentStatus?.fullPrice;
  const salePrice = priceByEnrollmentStatus?.salePrice;

  const isSalePrice = salePrice !== null && !!salePrice;
  const isDiscount = isSalePrice && salePrice !== fullPrice && (fullPrice || 0) > salePrice;

  const sendEventToGtm = () => {
    if (gtmContentId && courseDetailData) {
      const isCredit = [ACADEMIC_TYPE.CREDIT_PLUS, ACADEMIC_TYPE.REGRADE, ACADEMIC_TYPE.CREDIT].includes(
        textByEnrollmentStatus[enrollmentStatus].academicType as ACADEMIC_TYPE,
      );
      const academicType = isCredit ? ACADEMIC_TYPE.CREDIT : textByEnrollmentStatus[enrollmentStatus].academicType;
      const ga4Items = createGoogleAnalyticItem({
        courseDetail: courseDetailData,
        academicType: academicType as GtmAcademicType,
        courseName: name!,
      });
      sendAddToCardCourseDetail({
        value: salePrice || 0,
        currency: 'THB',
        items: [ga4Items],
      });
    }
  };

  const handleClickPurchase = async () => {
    if (!profile) {
      authorizeDialog.onOpen();
      return;
    }

    if (profile?.email.includes(profile.uid)) {
      confirmEmailDialog.onOpen();
      return;
    }

    const { academicType } = textByEnrollmentStatus[enrollmentStatus];

    if (academicType) {
      return router.push(ROUTES.COURSE.PAYMENT_ACADEMIC(idName, academicType));
    }

    console.error('COURSE_DETAIL::ACADEMIC_TYPE_NOT_FOUND');
  };

  const handleClickContinueLearning = () => {
    router.push(ROUTES.COURSE.LEARNING(idName));
  };

  const currentUrl = new URL(router.asPath, config.APP_DOMAIN);
  const pathname = router.asPath;

  const onAuthorize = () => {
    setAcademicEnrollCookie(pathname, purchaseType);
  };

  return (
    <Box sx={{ ...sx }}>
      <Box
        sx={{
          display: 'grid',
          minWidth: '293px',
          height: '100%',
          padding: { xs: '24px 16px', md: '24px' },
          border: `1px solid ${theme.palette.textOnPrimary.disabled}`,
          borderRadius: '8px',
          gridTemplateColumns: { xs: '1fr 6fr', md: '1fr' },
          gridTemplateAreas: {
            xs: '"icon title" "icon price" "button button"',
            md: '"icon" "title" "price" "content" "button"',
          },
        }}
      >
        <Box sx={{ gridArea: 'icon', fontSize: { xs: '24px', md: '32px' } }}>
          <FontAwesomeIcon icon={icon} />
        </Box>

        <Box sx={{ gridArea: 'title', display: 'flex', marginTop: { md: '24px', xs: 'unset' }, gap: '16px' }}>
          <Typography variant="h4">{title}</Typography>
          {!isAudit && (
            <Chip
              sx={{
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.grey[500]}`,
                height: '24px',
              }}
              label={t('course_detail.header_banner.purchase.recommend.text')}
            />
          )}
        </Box>

        <Box sx={{ gridArea: 'price' }}>
          {isEnrolledAudit ? (
            <EnrolledAuditBox />
          ) : (
            <PriceBox isDiscount={isDiscount} salePrice={salePrice} fullPrice={fullPrice} />
          )}
        </Box>

        <Box sx={{ gridArea: 'content', display: { xs: 'none', md: 'unset' } }}>
          <Typography variant="body1lanna">{content}</Typography>
        </Box>
        {!disabled && (
          <Box sx={{ gridArea: 'button', marginTop: '24px' }}>
            <Button
              color="secondary"
              variant={isAudit ? 'outlined' : 'contained'}
              onClick={isEnrolledAudit ? handleClickContinueLearning : handleClickPurchase}
              sx={{ height: { md: '60px', xs: '40px', padding: '8px 16px' } }}
              fullWidth
            >
              <Typography variant={isMobile ? 'button' : 'h4'}>{buttonText}</Typography>
            </Button>
          </Box>
        )}
      </Box>
      <AuthorizeModal
        open={authorizeDialog.isOpened}
        onClose={authorizeDialog.onClose}
        currentUrl={currentUrl.toString()}
        onAuthorize={onAuthorize}
      />
      <BaseModalWrapper
        open={confirmEmailDialog.isOpened}
        onClose={confirmEmailDialog.onClose}
        keepMounted={false}
        disabledBackDrop
        disabledEscapeKeyDown
      >
        <ConfirmEmailModal citizenId={idsProfile?.citizenId} dialog={confirmEmailDialog} hideCloseButton />
      </BaseModalWrapper>
    </Box>
  );
};

export default PurchaseButton;
